import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Award, CalendarPlus } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { NativeSelect } from '@/components/ui/native-select';
import { Skeleton } from '@/components/ui/skeleton';
import { Bild } from '@/components/TestimonialKarte';
import { fehlerText } from '@/lib/testimonialClient';

/* Testimonial-Bibliothek (Coach-Cockpit W1.7): freigegebene Ergebnisse mit Zahlen, Zeitraum und Foto-Ausschnitten ohne
   Gesicht — ohne Namen (der Server liefert keinen). Fotos nur mit Freigabe des Mitglieds. Im CRM (Leitung, Content-Team)
   und auf /coach (Overviewer, Master). „Als Idee in den Contentplan“ legt eine Idee ohne Namen und ohne Bild an. */

export const TestimonialSymbol = Award;   // Symbol für Menüs (/coach)

const MARKEN = [{ value: 'p1', label: 'P1' }, { value: 'ptl', label: 'PTL' }, { value: 'persoenlich', label: 'Persönlich' }];
const tag = (iso) => (iso ? new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '');

function Eintrag({ t, client, onNeu }) {
  const [marke, setMarke] = useState('p1');
  const [laeuft, setLaeuft] = useState(false);
  const idee = async () => {
    setLaeuft(true);
    try { await client.contentplan(t.id, marke); toast.success('Idee im Contentplan angelegt'); onNeu(); }
    catch (e) { toast.error(fehlerText(e, 'Nicht angelegt')); }
    setLaeuft(false);
  };
  return (
    <Card className="border-border/60" data-testid={`bibliothek-${t.id}`}>
      <CardContent className="space-y-3 p-4">
        {t.medien.length > 0 && (
          <div className={`grid gap-2 ${t.medien.length > 1 ? 'grid-cols-2' : 'grid-cols-1'}`}>
            {t.medien.map((m) => <Bild key={m.id} schluessel={m.id} laden={() => client.bild(m.id)} alt="Ergebnis, Ausschnitt ohne Gesicht" />)}
          </div>
        )}
        <p className="text-lg font-extrabold leading-snug">{t.text}</p>
        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <span>freigegeben {tag(t.freigegeben_am)}</span>
          <Badge variant="outline" className="text-[10px]">{t.medien.length ? `${t.medien.length} Foto(s)` : 'nur Zahlen'}</Badge>
        </div>
        {client.contentplan && (t.contentplan_idee
          ? <Badge variant="outline">Idee im Contentplan</Badge>
          : (
            <div className="flex flex-wrap items-center gap-2">
              <NativeSelect className="h-11 w-36" value={marke} onChange={setMarke} options={MARKEN} aria-label="Marke" />
              <Button variant="outline" className="min-h-11" onClick={idee} disabled={laeuft}><CalendarPlus className="mr-1.5 h-4 w-4" /> Als Idee in den Contentplan</Button>
            </div>
          ))}
      </CardContent>
    </Card>
  );
}

export default function TestimonialBibliothek({ client }) {
  const [liste, setListe] = useState(null);
  const [err, setErr] = useState('');
  const lade = useCallback(() => {
    client.liste().then((r) => { setListe(r.data.testimonials); setErr(''); }).catch((e) => setErr(fehlerText(e, 'Bibliothek nicht geladen')));
  }, [client]);
  useEffect(() => { lade(); }, [lade]);
  if (err) return <p className="text-sm text-muted-foreground" role="alert" data-testid="bibliothek-fehler">{err}</p>;
  if (!liste) return <div className="grid gap-4 md:grid-cols-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-56" />)}</div>;
  if (!liste.length) {
    return (
      <Card className="border-border/60"><CardContent className="flex items-center gap-3 p-6 text-sm text-muted-foreground">
        <Award className="h-5 w-5" /> Noch keine freigegebenen Ergebnisse. Testimonials entstehen in der Kundenakte, wenn das Mitglied einwilligt.
      </CardContent></Card>
    );
  }
  return (
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="testimonial-bibliothek">
      {liste.map((t) => <Eintrag key={t.id} t={t} client={client} onNeu={lade} />)}
    </div>
  );
}
